import React from "react";
import { cn } from "@/lib/utils";
import { ThumbsUp, ThumbsDown, Meh, Star, MessageSquare } from "lucide-react";

interface FeedbackRequestProps {
  onRating?: (rating: number) => void;
  rating?: number;
  animated?: boolean;
  className?: string;
}

export function FeedbackRequest({
  onRating,
  rating,
  animated = true,
  className,
}: FeedbackRequestProps) {
  const faces = [
    { value: 1, icon: ThumbsDown, label: "Nicht zufrieden", color: "text-red-500" },
    { value: 2, icon: Meh, label: "Neutral", color: "text-amber-500" },
    { value: 3, icon: ThumbsUp, label: "Zufrieden", color: "text-emerald-500" },
  ];

  return (
    <div className={cn("space-y-8", className)}>
      <div className="text-center">
        <h3 className="font-semibold text-xl">Wie war Ihre Beratung?</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Ihre Meinung ist uns wichtig
        </p>
      </div>

      {/* Rating Faces */}
      <div className={cn(
        "flex justify-center gap-8",
        animated && "animate-in fade-in slide-in-from-bottom-4"
      )}>
        {faces.map((face, index) => {
          const Icon = face.icon;
          const isSelected = rating === face.value;
          
          return (
            <button
              key={face.value}
              onClick={() => onRating?.(face.value)}
              className={cn(
                "flex flex-col items-center gap-2 p-4 rounded-xl transition-all",
                "hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary",
                isSelected && "bg-muted scale-110"
              )}
              style={animated ? { animationDelay: `${index * 100}ms` } : undefined}
            >
              <div className={cn(
                "w-16 h-16 rounded-full flex items-center justify-center transition-all",
                isSelected 
                  ? `bg-current/20 ${face.color}` 
                  : "bg-muted text-muted-foreground"
              )}>
                <Icon className={cn(
                  "h-10 w-10 transition-all",
                  isSelected ? face.color : ""
                )} />
              </div>
              <span className={cn(
                "text-sm font-medium",
                isSelected && face.color
              )}>
                {face.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Star Rating */}
      {rating && rating >= 2 && (
        <div className={cn(
          "text-center",
          animated && "animate-in fade-in slide-in-from-bottom-2"
        )}>
          <p className="text-sm text-muted-foreground mb-3">
            Würden Sie uns weiterempfehlen?
          </p>
          <div className="flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={cn(
                  "h-8 w-8 transition-colors cursor-pointer",
                  star <= (rating === 3 ? 5 : 3)
                    ? "fill-amber-400 text-amber-400"
                    : "text-muted-foreground/30"
                )}
              />
            ))}
          </div>
        </div>
      )}

      {/* Referral Section */}
      {rating === 3 && (
        <div className={cn(
          "bg-primary/5 border border-primary/20 p-6 rounded-xl text-center",
          animated && "animate-in fade-in zoom-in-95"
        )}>
          <MessageSquare className="h-10 w-10 text-primary mx-auto mb-3" />
          <h4 className="font-semibold text-lg mb-2">Kennen Sie jemanden, der auch profitieren könnte?</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Empfehlen Sie uns weiter und erhalten Sie eine Prämie!
          </p>
          <button className="bg-primary text-primary-foreground px-6 py-2 rounded-full font-medium hover:bg-primary/90 transition-colors">
            Jetzt empfehlen
          </button>
        </div>
      )}
    </div>
  );
}
